import { useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useSnackbar } from "notistack";

import {
  AlgoliaQuickSearch,
  type FacilityHit,
  createFacilityOnlySearchUiState,
  getFacilityDisplayName,
  isFacilityExactMatch,
  renderFacilityOnlyHit
} from "@/components/AlgoliaSearch";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Select from "@/components/Select";
import TextField from "@/components/TextField";

import SingleDatePicker from "../../person/_components/SingleDatePicker";
import type { JobTitle } from "../../person/queries";

const facilityIndexName = import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME;

type FieldValues = {
  facilityId: string;
  jobTitleId: string;
  customJobTitle: string;
  startDate: string;
  endDate: string;
  note: string;
};

export type NonMemberEmploymentFormData = {
  facilityId: string;
  jobTitle: string;
  startDate: string;
  endDate: string;
  note: string;
};

interface NonMemberEmploymentFormModalProps {
  open?: boolean;
  onClose(): void;
  headline: string;
  jobTitles?: JobTitle[];
  values?: FieldValues;
  initialFacilityName?: string;
  onSubmit(formData: NonMemberEmploymentFormData): Promise<{ success: boolean; message: string }>;
}

const defaultValues: FieldValues = {
  facilityId: "",
  jobTitleId: "",
  customJobTitle: "",
  startDate: "",
  endDate: "",
  note: ""
};

export const OTHER_JOB_TITLE_LABEL = "Other";

export default function NonMemberEmploymentFormModal({
  open,
  onClose,
  headline,
  jobTitles = [],
  values,
  initialFacilityName,
  onSubmit
}: NonMemberEmploymentFormModalProps) {
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { isValid, isSubmitting }
  } = useForm<FieldValues>({
    defaultValues,
    values,
    mode: "onChange"
  });

  const [bannerError, setBannerError] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  const submittingRef = useRef(false);

  const otherJobTitleId = useMemo(
    () => jobTitles.find((jobTitle) => jobTitle.jobTitle === OTHER_JOB_TITLE_LABEL)?.id,
    [jobTitles]
  );

  const selectedJobTitleId = useWatch({ control, name: "jobTitleId", defaultValue: values?.jobTitleId });
  const isOtherSelected = !!otherJobTitleId && selectedJobTitleId === otherJobTitleId;

  const jobTitleOptions = useMemo(
    () =>
      jobTitles
        .map((jobTitle) => ({
          label: jobTitle.jobTitle,
          value: jobTitle.id
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [jobTitles]
  );

  async function submit({ jobTitleId, customJobTitle, ...formData }: FieldValues) {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const selected = jobTitles.find((title) => title.id === jobTitleId);

      if (!selected) throw new Error("Job title is required");

      const isOther = selected.jobTitle === OTHER_JOB_TITLE_LABEL;
      const jobTitle = isOther ? customJobTitle.trim() : selected.jobTitle;

      if (!jobTitle) throw new Error("Job title is required");

      const { success, message } = await onSubmit({ ...formData, jobTitle });

      if (success) {
        enqueueSnackbar(message);
        onClose();
      } else {
        throw new Error(message);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setBannerError(`Error: ${message}`);
    } finally {
      submittingRef.current = false;
    }
  }

  return (
    <Dialog
      className="p-6 pr-2 max-h-[80vh] w-[648px] max-w-[90vw]"
      open={open}
      onClosed={onClose}
      headline={headline}
      hasFormElements
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="add-employment-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(submit)}
            testId="add-employment-save-button"
            disabled={isSubmitting || !isValid}
          >
            Save
          </Button>
        </>
      }
    >
      {!!bannerError && (
        <div className="mb-4">
          <Banner inline variant="error" description="The update failed." subText={bannerError} />
        </div>
      )}

      <div className="flex flex-col gap-6" data-testid="create-employment-modal">
        <Controller
          name="facilityId"
          control={control}
          rules={{ required: "Facility is required" }}
          render={({ field: { onChange }, fieldState: { error } }) => (
            <AlgoliaQuickSearch<FacilityHit>
              indexName={facilityIndexName}
              label="Facility Name"
              defaultValue={initialFacilityName}
              initialUiState={createFacilityOnlySearchUiState(facilityIndexName)}
              renderHit={renderFacilityOnlyHit}
              getDisplayValue={getFacilityDisplayName}
              isExactMatch={isFacilityExactMatch}
              onSelect={(facility) => onChange(facility.facility_id)}
              onClear={() => onChange("")}
              error={!!error}
              errorText={error?.message}
              noResultsMessage="No facilities found"
              required
            />
          )}
        />

        <Controller
          name="jobTitleId"
          control={control}
          rules={{ required: "Job title is required" }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              options={jobTitleOptions}
              value={value}
              onSelect={(selected) => {
                onChange(selected);
                if (selected !== otherJobTitleId) {
                  setValue("customJobTitle", "");
                }
                void trigger("customJobTitle");
              }}
              label="Job Title"
              required
              disabled={jobTitleOptions.length === 0}
              error={!!error}
              errorText={error?.message}
            />
          )}
        />

        {isOtherSelected && (
          <Controller
            name="customJobTitle"
            control={control}
            rules={{
              validate: (value, { jobTitleId }) =>
                !otherJobTitleId || jobTitleId !== otherJobTitleId || value.trim().length > 0 || "Job title is required"
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                type="text"
                label="Other Job Title"
                value={value || ""}
                onChangeText={onChange}
                required
                error={!!error}
                errorText={error?.message}
                className="w-full"
              />
            )}
          />
        )}

        <div className="grid grid-cols-2 gap-4">
          <SingleDatePicker control={control} label="Start Date" name="startDate" useModal />
          <SingleDatePicker control={control} label="End Date" name="endDate" useModal />
        </div>

        <Controller
          name="note"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="textarea"
              rows={4}
              label="Internal Note"
              value={value || ""}
              onChangeText={onChange}
              className="w-full"
            />
          )}
        />
      </div>
    </Dialog>
  );
}
